// @ts-nocheck
import * as React from 'react';
import _ from 'lodash';
import moment from 'moment';
import { toast } from "sonner";
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import { ColumnInterface } from '@/interfaces';
import { useModal } from "@/components/modal/provider";
import { ConfirmationDialog, ConfirmationDialogRaw } from '../Confirmation/Confirmation';
import { InformationCircleIcon } from '@heroicons/react/24/solid';
import Popover from '@mui/material/Popover';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from "@mui/material/Checkbox";
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import {
  DataGrid,
  GridRowModes,
  GridActionsCellItem,
  GridToolbarProps,
  GridToolbarContainer,
  useGridApiContext,
  useGridApiRef,
} from '@mui/x-data-grid';
import {
  randomCreatedDate,
  randomTraderName,
  randomId,
  randomArrayItem,
} from '@mui/x-data-grid-generator';

const roles = ['Market', 'Finance', 'Development'];
const randomRole = () => {
  return randomArrayItem(roles);
};

function DeleteDialog({ open, onClose, onDelete, id, row }) {
  const handleDelete = () => {
    // Deleting a player always soft-deletes the Player doc — which is what the
    // registration page reads (findAllByRegistrationId → Player.find) — so the
    // player disappears from the registration team too. We also always pass the
    // registrationTeam id to clean up the legacy embedded players array. No
    // separate "Also delete from registration team" opt-in is needed.
    onDelete(id, row.registrationTeam).then((res) => {
      console.log('handleDeleteClick::onDelete::res::', res);
      if (!res) {
        // The axios interceptor will 
        toast.error('Error deleting item');
        onClose();
        return;
      }
      if (res?.error) {
        toast.error(res?.error);
      } else {
        // va.track("Created Menu");
        toast.success(`Successfully deleted!`);
        onClose();
      }
    });
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 1, 
        fontSize: '1.25rem', 
        fontWeight: 600,
        color: 'text.primary'
      }}>
        Confirm Deletion
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <DialogContentText sx={{
          mb: 0,
          color: 'text.secondary',
          fontSize: '0.95rem'
        }}>
          Are you sure you want to delete <strong>{row.name}</strong>? This action cannot be undone.
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button 
          onClick={onClose}
          variant="outlined"
          sx={{ 
            textTransform: 'none',
            borderRadius: 1.5,
            px: 3
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleDelete}
          variant="contained"
          color="error"
          sx={{ 
            textTransform: 'none',
            borderRadius: 1.5,
            px: 3,
            boxShadow: '0 2px 8px var(--destructive)',
            '&:hover': {
              boxShadow: '0 2px 8px var(--destructive)'
            }
          }}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface EditToolbarProps extends GridToolbarProps {
  columns: any[];
}

function EditToolbar(props: EditToolbarProps) {
  const { columns } = props;
  const apiRef = useGridApiContext();

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

  const handlePopoverClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const createRow = () => {
    return _.reduce(columns, (result, column, index) => {
      let defaultValue;
      switch (column.type) {
        case 'string':
          defaultValue = '';
          break;
        case 'number':
          defaultValue = 0;
          break;
        case 'date':
          defaultValue = new Date();
          break;
        case 'boolean':
          defaultValue = true;
          break;
        case 'singleSelect':
          defaultValue = column.valueOptions[0];
          break;
        default:
          defaultValue = '';
          break;
      }
      result[column.field] = column.default || defaultValue;
      return result;
    }, {});
  };

  const handleClick = () => {
    const id = randomId();
    const newRow = { id, ...createRow(), isNew: true };
    apiRef.current.updateRows([{ ...newRow }]);
    // For cell editing mode, we don't need to start row edit mode
    // The user can click on any cell to start editing
  };

  return (
    <GridToolbarContainer sx={{ 
      py: 2, 
      px: 0,
      borderBottom: '1px solid',
      borderColor: 'divider',
      backgroundColor: 'background.paper'
    }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        color: 'text.secondary',
        fontSize: '0.875rem',
        fontWeight: 500
      }}>
        <span>Keyboard shortcuts:</span>
        <InformationCircleIcon 
          onClick={handlePopoverClick} 
          className="h-5 w-5 text-primary hover:cursor-pointer hover:text-primary-deep transition-colors" 
        />
      </Box>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px var(--shadow)',
          }
        }}
      >
        <Box sx={{ p: 3, maxWidth: 400 }}>
          <Box sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
            Keyboard Shortcuts
          </Box>
          <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
            <Box component="li" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <kbd style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '4px 8px',
                backgroundColor: 'var(--muted)',
                border: '1px solid var(--border)',
                borderRadius: '4px',
                fontSize: '0.75rem',
                fontFamily: 'monospace',
                minWidth: '24px',
                justifyContent: 'center'
              }}>
                <SaveIcon sx={{ fontSize: '0.875rem' }} />
              </kbd>
              <span style={{ fontSize: '0.875rem', color: 'var(--muted-foreground)' }}>Save changes</span>
            </Box>
            <Box component="li" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <kbd style={{
                display: 'inline-block',
                padding: '4px 8px',
                backgroundColor: 'var(--muted)',
                border: '1px solid var(--border)',
                borderRadius: '4px',
                fontSize: '0.75rem',
                fontFamily: 'monospace',
                minWidth: '24px',
                textAlign: 'center'
              }}>
                Esc
              </kbd>
              <span style={{ fontSize: '0.875rem', color: 'var(--muted-foreground)' }}>Revert changes</span>
            </Box>
            <Box component="li" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <kbd style={{
                display: 'inline-block',
                padding: '4px 8px',
                backgroundColor: 'var(--muted)',
                border: '1px solid var(--border)',
                borderRadius: '4px',
                fontSize: '0.75rem',
                fontFamily: 'monospace',
                minWidth: '24px',
                textAlign: 'center'
              }}>
                Tab
              </kbd>
              <span style={{ fontSize: '0.875rem', color: 'var(--muted-foreground)' }}>Keep changes</span>
            </Box>
            <Box component="li" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <kbd style={{
                display: 'inline-block',
                padding: '4px 8px',
                backgroundColor: 'var(--muted)',
                border: '1px solid var(--border)',
                borderRadius: '4px',
                fontSize: '0.75rem',
                fontFamily: 'monospace',
                minWidth: '24px',
                textAlign: 'center'
              }}>
                Enter
              </kbd>
              <span style={{ fontSize: '0.875rem', color: 'var(--muted-foreground)' }}>Keep changes & move to next cell</span>
            </Box>
            <Box component="li" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span style={{ fontSize: '0.875rem', color: 'var(--muted-foreground)' }}>Click outside cell to keep changes</span>
            </Box>
          </Box>
        </Box>
      </Popover>
    </GridToolbarContainer>
  );
}

export const convertToDataGrid = (columns: ColumnInterface[], dataList) => {
  return _.map(dataList, (data) => {
    const updatedRow = _.reduce(columns, (result, column, index) => {
      // console.log('convertToDataGrid:column::', column);
      if (column.type === 'date') {
        if(column.field.includes('__info_')) {
          const realField = column.field.replace('__info_', 'info.');
          console.log('realField::', realField, column.field);
          _.set(result, column.field, _.get(data, realField));
        } else {
          _.set(result, column.field, _.get(data, column.field) ? new Date( _.get(data, column.field)) : null);
          // result[column.field] = data[column.field] ? new Date(data[column.field]) : null;
        }
      } else {
        if(column.field.includes('__info_')) {
          const realField = column.field.replace('__info_', 'info.');
          console.log('realField::', realField, column.field);
          _.set(result, column.field, _.get(data, realField));
        } else {
          _.set(result, column.field, _.get(data, column.field));
        }
        // result[column.field] = data[column.field];
      }
      return result;
    }, {});
    return {
      id: data._id,
      ...data,
      ...updatedRow,
    };
  });
};

const revertFromDataGrid = (columns: ColumnInterface[], row) => {
    const updatedRow = _.reduce(columns, (result, column, index) => {
      if (column.type === 'date') {
        result[column.field] = row[column.field] ? moment(row[column.field]).utc().format('YYYY-MM-DDTHH:mm:ss.SSSZZ') : null;
      } else {
        result[column.field] = row[column.field];
      }
      return result;
    }, {});
    return {
      ..._.omit(row, ['id', 'isNew', 'isEdited']),
      ...updatedRow,
    };
};

interface DataTableConfig {
  onEditSettings?: (id: string) => void;
  onEditPayment?: (id: string) => void;
  onEditAdmins?: (id: string) => void;
  onEditTiebreakers?: (id: string) => boolean;
  onClone?: (id: string) => void;
  isTiebreakersEnabled?: (id: string) => boolean;
  pageSize?: number;
}

export function DataTable({ columns, dataList, onUpdate, onAdd, onDelete, config = {} as DataTableConfig }) {
  // console.log('columns:: ', columns);
  // console.log('dataList:: ', dataList);
  const [rows, setRows] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [deleteId, setDeleteId] = React.useState(null);
  const [deleteRow, setDeleteRow] = React.useState({ name: ''});
  const [anchorEls, setAnchorEls] = React.useState<{ [key: string]: HTMLElement | null }>({});
  const apiRef = useGridApiRef();

  // Calculate dynamic height based on page size
  const pageSize = config.pageSize || 25;
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: pageSize,
  });
  
  const rowHeight = 52; // MUI DataGrid default row height
  const headerHeight = 56; // MUI DataGrid default header height
  const toolbarHeight = 64; // Height for the toolbar (EditToolbar)
  const footerHeight = 52; // MUI DataGrid default footer height
  const borderHeight = 2; // Border height
  
  // Calculate the actual number of rows that will be displayed
  const actualRowsToDisplay = Math.min(rows.length, paginationModel.pageSize);
  
  // Calculate total height needed for the actual number of rows
  const calculatedHeight = headerHeight + toolbarHeight + (rowHeight * actualRowsToDisplay) + footerHeight + borderHeight;

  // Handler to open the menu
  const handleMoreClick = (event: React.MouseEvent<HTMLElement>, id: string) => {
    setAnchorEls((prev) => ({ ...prev, [id]: event.currentTarget }));
  };

  // Handler to close the menu
  const handleMoreClose = (id: string) => {
    setAnchorEls((prev) => ({ ...prev, [id]: null }));
  };

  // Handler for "Edit Settings" action
  const handleEditSettings = (id: string) => {
    // Implement your "Edit Settings" logic here
    console.log(`Edit Settings clicked for row with id: ${id}`);
    if (config.onEditSettings) {
      config.onEditSettings(id);
    }
    handleMoreClose(id);
  };

  const handleEditAdmins = (id: string) => {
    console.log(`Edit Admins clicked for row with id: ${id}`);
    if (config.onEditAdmins) {
      config.onEditAdmins(id);
    }
    handleMoreClose(id);
  };

  const handleEditPayment = (id) => {
    console.log('handleEditPayment::', id);
    if (config.onEditPayment) {
      config.onEditPayment(id);
    }
    handleMoreClose(id);
  };

  const handleEditTiebreakers = (id: string) => {
    console.log('handleEditTiebreakers::', id);
    if (config.onEditTiebreakers) {
      config.onEditTiebreakers(id);
    }
    handleMoreClose(id);
  };

  const handleClone = (id: string) => {
    console.log('handleClone::', id);
    if (config.onClone) {
      config.onClone(id);
    }
    handleMoreClose(id);
  };

  React.useEffect(() => {
    console.log('DataTable::useEffect');
    const merged = _.map(dataList, (data, key) => {
      const isEdited = _.get(rows[key], 'isEdited', false);
      return isEdited ? rows[key] : data;
    });
    setRows(merged);
  }, [dataList]);

  // Update pagination model when config.pageSize changes
  React.useEffect(() => {
    if (config.pageSize && config.pageSize !== paginationModel.pageSize) {
      setPaginationModel(prev => ({
        ...prev,
        pageSize: config.pageSize,
        page: 0, // Reset to first page when page size changes
      }));
    }
  }, [config.pageSize]);



  console.log('rows::: ', rows);

  const [rowModesModel, setRowModesModel] = React.useState({});
  const [selectedCell, setSelectedCell] = React.useState<{ id: string; field: string } | null>(null);

  const handleRowEditStop = (params, event) => {
    console.log('handleRowEditStop::params::', params);
    // if (params.reason === GridRowEditStopReasons.rowFocusOut) {
    //   event.defaultMuiPrevented = true;
    // }
  };

  const handleEditClick = (data) => () => {
    const { id, row } = data;
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
    console.log('handleEditClick');
  };

  const handleSaveClick = (data) => () => {
    const { id, row } = data;
    const clonedRow = _.cloneDeep(row);
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
    console.log('handleSaveClick', clonedRow, data);
    if (clonedRow.isNew) {
      console.log('handleSaveClick::isNew', clonedRow);
      delete clonedRow.isNew;
      setRows(rows.map((row) => (row.id === id ? clonedRow : row)));
      onAdd(revertFromDataGrid(columns, clonedRow));
    } else {
      console.log('handleSaveClick::isEdited', clonedRow);
      delete clonedRow.isEdited;
      setRows(rows.map((row) => (row.id === id ? clonedRow : row)));
      onUpdate(revertFromDataGrid(columns, clonedRow)).then((res) => {
        console.log('handleSaveClick::onUpdate::res::', res);
        if (res?.error) {
          toast.error(res?.error);
        }
      });

    }
  };

  const handleDeleteClick = (id, row) => () => {
    // setRows(rows.filter((row) => row.id !== id));
    console.log('handleDeleteClick');
    setDeleteId(id);
    setDeleteRow(row);
    setOpen(true);

    const r = false;
    // var r = confirm(`Are you sure you want to delete ${row.name}?`);
    if (!!r == true) {
      onDelete(id).then((res) => {
        console.log('handleDeleteClick::onDelete::res::', res);
        if (res?.error) {
          toast.error(res?.error);
        } else {
          // va.track("Created Menu");
          setRows(rows.filter((row) => row.id !== id));
          toast.success(`Successfully deleted!`);
        }
      });
    }
  };

  const handleCancelClick = (id) => () => {
    console.log('handleCancelClick', id);
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows.find((row) => row.id === id);
    if (editedRow.isNew) {
      setRows(rows.filter((row) => row.id !== id));
    }
    if (editedRow.isEdited) {
      const originalRow = _.find(dataList, { _id: id });
      setRows(rows.map((row) => (row.id === id ? originalRow : row)));
    }
  };

  const processRowUpdate = (newRow, oldRow) => {
    console.log('processRowUpdate', newRow, _.isEqual(newRow, oldRow), oldRow);
    let updatedRow;
    if (newRow?._id) {
      if (!_.isEqual(newRow, oldRow)) {
        updatedRow = { ...newRow, isEdited: true }; // edited row
        console.log('onUpdateNew??', updatedRow);
      } else {
        updatedRow = { ...newRow }; // no change
      }
    } else {
      updatedRow = { ...newRow, isNew: true }; // new row
    }
    // const updatedRow = newRow?._id && !_.isEqual(newRow, oldRow) ? { ...newRow, isEdited: true } : { ...newRow, isNew: true };
    setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
    return updatedRow;
  };

  const handleRowModesModelChange = (newRowModesModel, foo) => {
    console.log('handleRowModesModelChange::newRowModesModel::', newRowModesModel, foo);
    setRowModesModel(newRowModesModel);
  };

  const handleCellClick = (params) => {
    // Don't set selection if the cell is being edited
    if (params.field === 'actions') return;
    
    setSelectedCell({ id: params.id, field: params.field });
  };

  const handleRowClick = () => {
    // Clear selection when clicking on row (outside of cells)
    setSelectedCell(null);
  };

  // Add cellClassName to all columns for selection highlighting
  const columnsWithSelection = columns.map(column => ({
    ...column,
    cellClassName: (params) => {
      const isSelected = selectedCell && 
        selectedCell.id === params.id && 
        selectedCell.field === params.field;
      return isSelected ? 'cell-selected' : '';
    }
  }));

  // The MoreVert (3-dots) menu items (Settings / Payment / Admins / Tiebreakers /
  // Clone) are only functional when the consumer wires up the matching config
  // callback. Grids that pass none (e.g. the team-page players table) get a dead
  // menu, so only render it when at least one action is actually configured.
  const hasMoreActions = !!(
    config.onEditSettings ||
    config.onEditPayment ||
    config.onEditAdmins ||
    config.onEditTiebreakers ||
    config.onClone
  );

  const actions = {
    field: 'actions',
    type: 'actions',
    headerName: 'Actions',
    width: 120,
    cellClassName: 'actions',
    getActions: (data) => {
      const { id, row } = data;
      const isNew = row?.isNew;
      const isEdited = row?.isEdited;

      return [
        // Actions for cell editing mode
        <GridActionsCellItem
          icon={<SaveIcon sx={{ 
            color: isEdited ? 'primary.main' : 'text.secondary',
            fontSize: '1.25rem'
          }} />}
          label="Save"
          onClick={handleSaveClick(data)}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon sx={{ 
            color: 'text.secondary',
            fontSize: '1.25rem'
          }} />}
          label="Delete"
          onClick={handleDeleteClick(id, row)}
        />,
        // New MoreVert Action (only when the consumer configures a menu action)
        ...(hasMoreActions ? [<div key="more-actions">
          <MoreVertIcon
            aria-controls={`action-menu-${id}`}
            aria-haspopup="true"
            onClick={(event: any) => handleMoreClick(event, id)}
            style={{ 
              cursor: 'pointer',
              color: 'var(--muted-foreground)',
              fontSize: '1.25rem'
            }}
            sx={{
              '&:hover': {
                color: 'primary.main',
                backgroundColor: 'action.hover',
                borderRadius: '4px',
                padding: '2px'
              }
            }}
          />
          <Menu
            id={`action-menu-${id}`}
            anchorEl={anchorEls[id]}
            open={Boolean(anchorEls[id])}
            onClose={() => handleMoreClose(id)}
            PaperProps={{
              sx: {
                borderRadius: 2,
                boxShadow: '0 8px 32px var(--shadow)',
                border: '1px solid',
                borderColor: 'divider',
                mt: 1
              }
            }}
          >
            <MenuItem 
              onClick={() => handleEditSettings(id)}
              sx={{ 
                fontSize: '0.875rem',
                py: 1.5,
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
            >
              Settings
            </MenuItem>
            <MenuItem 
              onClick={() => handleEditPayment(id)}
              sx={{ 
                fontSize: '0.875rem',
                py: 1.5,
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
            >
              Payment
            </MenuItem>
            <MenuItem 
              onClick={() => handleEditAdmins(id)}
              sx={{ 
                fontSize: '0.875rem',
                py: 1.5,
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
            >
              Admins
            </MenuItem>
            <Tooltip 
              title={config.isTiebreakersEnabled && !config.isTiebreakersEnabled(id) ? "Add sports to this tournament to enable tiebreaker configuration" : ""}
              placement="left"
              disableHoverListener={!config.isTiebreakersEnabled || config.isTiebreakersEnabled(id)}
            >
              <span>
                <MenuItem 
                  onClick={() => handleEditTiebreakers(id)} 
                  disabled={!config.onEditTiebreakers || (config.isTiebreakersEnabled ? !config.isTiebreakersEnabled(id) : false)}
                  sx={{ 
                    fontSize: '0.875rem',
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    },
                    '&.Mui-disabled': {
                      opacity: 0.5
                    }
                  }}
                >
                  Tiebreakers
                </MenuItem>
              </span>
            </Tooltip>
            <MenuItem 
              onClick={() => handleClone(id)}
              sx={{ 
                fontSize: '0.875rem',
                py: 1.5,
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
            >
              Clone
            </MenuItem>
          </Menu>
        </div>] : []),
      ];
    },
  };

  return (
    <Box
      sx={{
        height: calculatedHeight,
        width: '100%',
        backgroundColor: 'background.paper',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
        '& .MuiDataGrid-root': {
          border: 'none',
          backgroundColor: 'transparent',
        },
        '& .MuiDataGrid-cell': {
          borderBottom: '1px solid',
          borderColor: 'divider',
          fontSize: '0.875rem',
          color: 'text.primary',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        },
        '& .MuiDataGrid-columnHeaders': {
          backgroundColor: 'var(--muted)',
          borderBottom: '2px solid',
          borderColor: 'divider',
          '& .MuiDataGrid-columnHeader': {
            fontSize: '0.875rem',
            fontWeight: 600,
            color: 'var(--foreground)',
            borderRight: '1px solid',
            borderColor: 'divider',
            '&:last-child': {
              borderRight: 'none',
            }
          }
        },
        '& .MuiDataGrid-row': {
          '&:hover': {
            backgroundColor: 'action.hover',
          },
          // Remove row-level selection highlighting
          '&.Mui-selected': {
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'action.hover',
            }
          }
        },
        // Add cell-level highlighting for editing cells
        '& .MuiDataGrid-cell--editing': {
          backgroundColor: 'var(--accent)',
          boxShadow: '0 0 0 2px var(--ring)',
          '&:focus-within': {
            backgroundColor: 'var(--accent)',
            boxShadow: '0 0 0 2px var(--ring)',
          }
        },
        // Ensure non-editing cells in editing rows don't get highlighted
        '& .MuiDataGrid-row--editing .MuiDataGrid-cell:not(.MuiDataGrid-cell--editing)': {
          backgroundColor: 'transparent',
        },

        '& .actions': {
          color: 'text.secondary',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
        },
        '& .textPrimary': {
          color: 'text.primary',
        },
        '& .MuiDataGrid-cell:focus': {
          outline: 'none',
        },
        '& .MuiDataGrid-cell:focus-within': {
          outline: 'none',
        },
        '& .MuiDataGrid-columnHeader:focus': {
          outline: 'none',
        },
        '& .MuiDataGrid-columnHeader:focus-within': {
          outline: 'none',
        }
      }}
    >
      <DeleteDialog
        open={open}
        onClose={() => {
          setOpen(false);
          setDeleteId(null);
          setDeleteRow({name: ''});
        }}
        onDelete={onDelete}
        id={deleteId}
        row={deleteRow} 
      />
      <DataGrid
        apiRef={apiRef}
        rows={rows}
        columns={columnsWithSelection.concat([actions])}
        editMode="cell"
        onRowEditStop={handleRowEditStop}
        onCellClick={handleCellClick}
        onRowClick={handleRowClick}
        processRowUpdate={processRowUpdate}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[25, 50, 100]}
        slots={{
          toolbar: EditToolbar,
        }}
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        sx={{
          '& .MuiDataGrid-virtualScroller': {
            backgroundColor: 'background.paper',
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'var(--muted)',
          },
          // Additional styling for cell editing
          '& .MuiDataGrid-cell:focus': {
            outline: 'none',
          },
          '& .MuiDataGrid-cell:focus-within': {
            outline: 'none',
          },
          // Ensure editing cells have proper styling
          '& .MuiDataGrid-cell--editing': {
            backgroundColor: 'var(--accent)',
            boxShadow: '0 0 0 2px var(--ring)',
            position: 'relative',
            zIndex: 1,
          },
          // Style the input field within editing cells
          '& .MuiDataGrid-cell--editing input': {
            backgroundColor: 'transparent',
            border: 'none',
            outline: 'none',
            width: '100%',
            height: '100%',
            padding: '0',
            fontSize: 'inherit',
            fontFamily: 'inherit',
          },
          // Ensure non-editing cells in the same row don't get highlighted
          '& .MuiDataGrid-row:has(.MuiDataGrid-cell--editing) .MuiDataGrid-cell:not(.MuiDataGrid-cell--editing)': {
            backgroundColor: 'transparent',
          },
          // Add a subtle border to the editing cell for better visibility
          '& .MuiDataGrid-cell--editing::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            border: '2px solid',
            borderColor: 'primary.main',
            borderRadius: '4px',
            pointerEvents: 'none',
            zIndex: 0,
          },
          // Add visual indicator for selected cells (not editing)
          '& .cell-selected': {
            backgroundColor: 'action.selected',
            border: '2px solid',
            borderColor: 'primary.main',
            borderRadius: '4px',
            position: 'relative',
            zIndex: 1,
          }
        }}
      />
    </Box>
  );
}
